import React, { useState, useEffect, useRef, useCallback } from "react";
import { Outlet } from "react-router-dom";
import "./LandingPage.scss";
import Sidebar from "./Sidebar";
import MarketInfoLayer from "./MarketInfoLayer";
import TelegramPanel from "../../Pages/Dashboard/TelegramDashboard";
import { get } from "../../services/smartApiService";
import { LOGIN_URLS } from "../../api/LoginUrls";
import { AI_TRADER_URLS } from "../../api/AiTraderUrls";

const POLL_INTERVAL = 60000; // 1 minute
const ORIGINAL_TITLE = document.title;

// Short notification beep (base64-encoded WAV)
const NOTIFICATION_SOUND = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdW2JkYuCc2xzeoKLj4qBdW9zeX+GioiFfHRxdXuBhomIhH14dHZ6f4OGh4WBfHl3eHt+goSFhIJ/fXt6e31/goODg4KAf356ent8f4GCg4OBgH9+fXx8fX+AgoKCgoGAf359fHx9f4CCgoKBgYB/fn19fX5/gYGBgoGBgH9+fn19fn+AgYGBgYGAf39+fn1+f4CBgYGBgICAf35+fn5/gIGBgYGAgIB/f35+fn9/gIGBgYCAgIB/f39+fn9/gICBgYCAgIB/f39/fn9/gICAgYCAgICAf39/f3+AgICAgICAgIB/f39/f4CAgICAgICAgH9/f39/gICAgICAgICAf39/f3+AgICAgICAgIB/f39/f4CAgICAgICAgH9/f39/gA==";

const playNotificationSound = () => {
  try {
    const audio = new Audio(NOTIFICATION_SOUND);
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch {}
};

const updateTabTitle = (count: number) => {
  document.title = count > 0 ? `(${count}) ${ORIGINAL_TITLE}` : ORIGINAL_TITLE;
};

const HomeLayout: React.FC = () => {
  const [telegramOpen, setTelegramOpen] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const prevUnreadRef = useRef(-1); // -1 = first load, skip sound
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectedRef = useRef(false);
  const prevSignalRef = useRef<Record<string, string>>({});

  const pollDialogs = useCallback(() => {
    get(LOGIN_URLS.TELEGRAM_STATUS, { showLoading: false, handleError: () => {} }).subscribe({
      next: (response: any) => {
        if (!response.data?.connected) {
          isConnectedRef.current = false;
          setTotalUnread(0);
          updateTabTitle(0);
          return;
        }
        isConnectedRef.current = true;
        get(LOGIN_URLS.TELEGRAM_DIALOGS, { showLoading: false, handleError: () => {} }).subscribe({
          next: (res: any) => {
            const dialogs = res.data?.dialogs || [];
            const newTotal = dialogs.reduce((sum: number, d: any) => sum + (d.unread_count || 0), 0);

            // Play sound + browser notification if count increased (skip first load)
            if (prevUnreadRef.current >= 0 && newTotal > prevUnreadRef.current) {
              playNotificationSound();
              if (Notification.permission === "granted") {
                new Notification("Telegram", {
                  body: `You have ${newTotal} unread message${newTotal > 1 ? "s" : ""}`,
                });
              } else if (Notification.permission !== "denied") {
                Notification.requestPermission();
              }
            }

            prevUnreadRef.current = newTotal;
            setTotalUnread(newTotal);
            updateTabTitle(newTotal);
          },
          error: () => {},
        });
      },
      error: () => {},
    });
  }, []);

  // Poll AI Trader signals for direction changes every 10 min
  const pollAISignals = useCallback(() => {
    get(AI_TRADER_URLS.SIGNAL_ALL, { showLoading: false, handleError: () => {} }).subscribe({
      next: (res: any) => {
        const signals = res.data || {};
        ['NIFTY', 'BANKNIFTY'].forEach((sym) => {
          const sig = signals[sym];
          if (!sig) return;
          const prev = prevSignalRef.current[sym];
          if (prev && prev !== sig.direction && sig.direction !== 'WAIT') {
            playNotificationSound();
            if (Notification.permission === 'granted') {
              new Notification(`${sym} Signal Changed!`, {
                body: `${prev} → ${sig.direction} | Strike: ${sig.strike} | Confidence: ${sig.confidence}/10`,
                icon: '/favicon.ico',
              });
            }
          }
          prevSignalRef.current[sym] = sig.direction;
        });
      },
      error: () => {},
    });
  }, []);

  // Start background polling on mount
  useEffect(() => {
    pollDialogs();
    pollRef.current = setInterval(pollDialogs, POLL_INTERVAL);

    // Poll AI signals every 30 minutes (matches backend signal cron cadence)
    const signalPoll = setInterval(pollAISignals, 30 * 60 * 1000);
    pollAISignals(); // initial fetch to set baseline

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      clearInterval(signalPoll);
      document.title = ORIGINAL_TITLE;
    };
  }, [pollDialogs, pollAISignals]);

  // Re-poll when panel closes (after mark-as-read has time to propagate)
  useEffect(() => {
    if (!telegramOpen && isConnectedRef.current) {
      const timer = setTimeout(pollDialogs, 2000);
      return () => clearTimeout(timer);
    }
  }, [telegramOpen, pollDialogs]);

  // Callback from TelegramPanel to sync unread counts immediately
  const handleUnreadUpdate = useCallback((count: number) => {
    prevUnreadRef.current = count;
    setTotalUnread(count);
    updateTabTitle(count);
  }, []);

  const isMobile = window.innerWidth < 768;

  return (
    <div className="home-layout-wrapper" style={{ display: 'flex', flexDirection: 'row', minHeight: '100vh', background: '#080810' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <MarketInfoLayer />
        <main className="main-content" style={{ flex: 1, background: '#080810', paddingBottom: isMobile ? 54 : 0 }}>
          <Outlet />
        </main>
      </div>

      {/* Floating Telegram Icon */}
      <button
        className={`tg-floating-btn ${telegramOpen ? "tg-floating-active" : ""}`}
        onClick={() => setTelegramOpen(!telegramOpen)}
        title="Telegram Messages"
      >
        {telegramOpen ? (
          <i className="fa fa-times" style={{ fontSize: "1.2rem" }}></i>
        ) : (
          <i className="fab fa-telegram" style={{ fontSize: "1.4rem" }}></i>
        )}
        {!telegramOpen && totalUnread > 0 && (
          <span className="tg-floating-badge">{totalUnread > 99 ? "99+" : totalUnread}</span>
        )}
      </button>

      {/* Telegram Side Panel */}
      <TelegramPanel
        isOpen={telegramOpen}
        onClose={() => setTelegramOpen(false)}
        onUnreadUpdate={handleUnreadUpdate}
      />
    </div>
  );

};

export default HomeLayout;
